import type { TimelineEntry } from '@zealot/ui/components/activity/timeline-entry';
import { TimelineEventRow } from '@zealot/ui/components/activity/timeline-event-row';
import { TimelineNoteCard } from '@zealot/ui/components/activity/timeline-note-card';
import { TimelineRail } from '@zealot/ui/components/activity/timeline-rail';
import { Button } from '@zealot/ui/components/button';
import { HStack } from '@zealot/ui/components/hstack';
import { Skeleton } from '@zealot/ui/components/skeleton';
import { type ReactNode, useEffect, useState } from 'react';

export type {
  TimelineChange,
  TimelineEntry,
  TimelineEntryBadge,
  TimelineEntryIcon,
  TimelineEntryKind,
  TimelineEntryTone,
} from '@zealot/ui/components/activity/timeline-entry';

// gloss
type ActivityTimelineProps = {
  entries: TimelineEntry[];
  isLoading?: boolean;
  isError?: boolean;
  emptyLabel?: string;
  errorLabel?: string;
  initialVisible?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreLabel?: string;
  renderEntryAction?: (entry: TimelineEntry) => ReactNode;
};

const SKELETON_ROWS = [0, 1, 2, 3];

// gloss
export const ActivityTimeline = ({
  entries,
  isLoading,
  isError,
  emptyLabel = 'No activity yet',
  errorLabel = "Couldn't load activity",
  initialVisible,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  loadMoreLabel = 'Load earlier',
  renderEntryAction,
}: ActivityTimelineProps) => {
  const canCollapse = initialVisible !== undefined && entries.length > initialVisible;
  const [expanded, setExpanded] = useState(false);

  const newestEntryId = entries[entries.length - 1]?.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on the newest id, not the array ref.
  useEffect(() => {
    setExpanded(false);
  }, [newestEntryId]);

  if (isError) {
    return (
      <p className="text-destructive text-sm" data-slot="activity-timeline-error">
        {errorLabel}
      </p>
    );
  }

  if (isLoading) {
    return (
      <ul className="select-none" data-slot="activity-timeline" aria-hidden="true">
        {SKELETON_ROWS.map((row, index) => (
          <HStack key={row} gap={3} align="stretch" render={<li data-slot="activity-timeline-entry" />}>
            <TimelineRail isLast={index === SKELETON_ROWS.length - 1} muted />
            <div className="min-w-0 flex-1 pb-4">
              <Skeleton className="h-2.5 w-32" />
              <Skeleton className="mt-1.5 h-2 w-20" />
            </div>
          </HStack>
        ))}
      </ul>
    );
  }

  if (entries.length === 0 && !hasMore) {
    return (
      <p className="text-muted-foreground text-sm" data-slot="activity-timeline-empty">
        {emptyLabel}
      </p>
    );
  }

  const hiddenCount = canCollapse && !expanded ? entries.length - (initialVisible as number) : 0;
  const visible = hiddenCount > 0 ? entries.slice(-(initialVisible as number)) : entries;

  const isCollapsed = canCollapse && !expanded;
  const showLoadMore = hasMore && Boolean(onLoadMore) && !isCollapsed;

  return (
    <div data-slot="activity-timeline">
      {}
      {(canCollapse || showLoadMore) && (
        <HStack gap={1} className="mb-2">
          {canCollapse && (
            <Button
              variant="link"
              size="sm"
              className="px-0"
              onClick={() => setExpanded((prev) => !prev)}
              data-slot="activity-timeline-toggle"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} earlier event${hiddenCount === 1 ? '' : 's'}`}
            </Button>
          )}
          {showLoadMore && (
            <Button
              variant="link"
              size="sm"
              className="px-0"
              onClick={onLoadMore}
              disabled={isLoadingMore}
              data-slot="activity-timeline-load-more"
            >
              {isLoadingMore ? 'Loading…' : loadMoreLabel}
            </Button>
          )}
        </HStack>
      )}
      <ul data-slot="activity-timeline-list">
        {visible.map((entry, index) => {
          const action = renderEntryAction?.(entry);
          const isNote = entry.kind === 'note';
          return (
            <HStack key={entry.id} gap={3} align="stretch" render={<li data-slot="activity-timeline-entry" />}>
              <TimelineRail
                isLast={index === visible.length - 1}
                tone={entry.tone}
                align={isNote ? 'card' : 'row'}
                icon={entry.icon}
              />
              {isNote ? (
                <TimelineNoteCard entry={entry} action={action} />
              ) : (
                <TimelineEventRow entry={entry} action={action} />
              )}
            </HStack>
          );
        })}
      </ul>
    </div>
  );
};
